/**
 * THE TEMPLATE LIST IS WHATEVER SFMC SAYS IT IS (2026-08-19, per Jack: "remove
 * all the hard coded templates and only pull in the sfmc blocks/template under
 * the template tab").
 *
 * DELIBERATE DIVERGENCE FROM THE MONOLITH. `mvpTplGrid` (index.html L4891–4917)
 * hard-codes five families — label, name-match regex, catalog name, colour and a
 * literal eblast count — and the port copied that table verbatim. It is gone here
 * and it is not coming back: a template is ONE `ECH_TPL_*` block in Content
 * Builder, so adding, renaming or retiring one is Layne's edit, not a release.
 * Do not "restore parity" on this file.
 *
 * What that costs, stated plainly rather than papered over:
 *   • the per-family EBLAST COUNTS (234/153/152/135/118) were a 2026-07-25 snapshot
 *     of a Creative Template Dashboard ranking. There is no live equivalent on this
 *     path, so no number replaces them — a card shows the live BLOCK COUNT instead,
 *     which is read straight off the template's own sequence.
 *   • the family COLOURS were only ever the gradient tile's fill. The tile went with
 *     them; a template that cannot be rendered gets no thumbnail rather than a
 *     coloured rectangle standing in for artwork nobody has seen.
 *
 * `echPreviewHtml` is what a card (and the form's preview rail) actually shows: the
 * template assembled from Layne's REAL blocks and run through the same pure-TS
 * renderer the Studio and the export use. Not a thumbnail of something else, not a
 * catalog lookup by fuzzy name — the email the Studio is about to build.
 */
import { useCallback, useEffect, useState } from "react";
import { FULL_REGISTRY } from "../email/blocks";
import { renderDocument, type AnyBlockInstance } from "../email/document";
import { echAdopt, echAdoptSnapshot, echLibReady, echShellFor } from "./echSnap";
import { echMeta } from "./echRegistry";
import { echDoc, echSeq, echTemplateNames, echTplVersion } from "./echTemplates";
import { echFillAll, echLevel, echScopeReady, echSeedStrip, echPlanCardsNeeded, echPlanCardsExcess, echLastPlanCardIdx, ECH_FEAT_KEY, type DynBlock, type EchCtx } from "./echDyn";
import { sfmcBlocksOnce, __resetSfmcSource } from "./sfmcSource";

/** `loading` covers the 16–60s cold pull; `error` means /sfmc-blocks did not answer
 *  AND there was no snapshot to fall back on. There is no fourth state where a
 *  hard-coded list stands in. */
export type EchListStatus = "loading" | "ready" | "error";

export interface EchTemplateList {
  /** canonical template names, in the order SFMC returned their definition blocks */
  names: string[];
  status: EchListStatus;
  /** bumped on every load, so consumers can re-derive assembled previews */
  version: number;
  /** drop the cached pull and go again */
  retry: () => void;
}

/**
 * Load the block library once and expose the templates it declares.
 *
 * Both the registration and the template load run here, in that order: a
 * placement resolves to a REAL block only if the block is registered first
 * (`echDoc`), so the previews would otherwise be placeholder cards.
 */
export function useEchTemplateList(): EchTemplateList {
  const [state, setState] = useState<{ names: string[]; status: EchListStatus; version: number }>(() => {
    /* SYNCHRONOUS, IN THE INITIALISER, BEFORE FIRST PAINT. This is what makes the
       tab list appear instantly on a warm browser instead of after the 16–60s pull
       (`echSnap.ts`). A no-op when a live payload has already been adopted, so a
       stale snapshot can never take the list back off a real answer. */
    echAdoptSnapshot();
    return {
      /* a second mount must not flash "Loading" over a library that is already in
         memory — the registry is module-scope and outlives any component */
      names: echTemplateNames(),
      status: echTplVersion() > 0 ? "ready" : "loading",
      version: echTplVersion(),
    };
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let dead = false;
    sfmcBlocksOnce()
      .then((blocks) => {
        /* re-registers (and so bumps the versions) only when the bytes moved */
        echAdopt(blocks, "sfmc");
        if (!dead) setState({ names: echTemplateNames(), status: "ready", version: echTplVersion() });
      })
      /* A FAILED REFRESH KEEPS THE SNAPSHOT (echSnap rule 2). The list is only
         `error` when there is nothing to show — with blocks in hand the tab stays
         usable and the failure is invisible, which is the correct outcome for a
         payload that is minutes old. */
      .catch(() => { if (!dead) setState((s) => ({ ...s, status: echLibReady() ? "ready" : "error" })); });
    return () => { dead = true; };
  }, [nonce]);

  const retry = useCallback(() => {
    __resetSfmcSource();
    setState((s) => ({ ...s, status: "loading" }));
    setNonce((n) => n + 1);
  }, []);

  return { names: state.names, status: state.status, version: state.version, retry };
}

/** how many blocks a template places — the live sequence's length, nothing derived */
export function echBlockCount(name: string): number {
  const s = echSeq(name);
  return s ? s.length : 0;
}

/* a preview document is never edited, so it needs ids only to be a valid document */
let pvId = 0;
const pvBlock = (type: string): AnyBlockInstance => {
  const b: AnyBlockInstance = { id: "echpv" + ++pvId, type, data: FULL_REGISTRY[type].def() as Record<string, unknown> };
  /* same rule as the Studio's makeBlock: a seed's authored default is a hint, so
     the preview must not render "City, State" where the Studio renders blank */
  echSeedStrip(b);
  return b;
};

/**
 * The template, assembled from its live SFMC blocks and rendered.
 * Returns "" when the name is not a loaded template, or the library has not
 * landed yet — the caller shows a loading/absent state, never a stand-in.
 */
export function echPreviewHtml(name: string): string {
  return echPreviewHtmlFor(name, null);
}

/**
 * THE SAME TEMPLATE, WITH STEP 1'S ANSWERS IN IT (2026-08-20 — Jack: "i'd also
 * like this preview if possible to update dynamic info when someone is filling out
 * the form such as division code and community logo header updates and etc.").
 *
 * `echPreviewHtml` above ran `echDoc → renderDocument` with nothing in between, so
 * the rail showed Layne's shipped placeholders ("Community Name | 000-000-0000")
 * however much of Step 1 was filled in — while the Studio canvas, which assembles
 * the SAME template from the SAME blocks, had been filling since 2026-08-19. Two
 * renderings of one document that disagreed.
 *
 * IT CALLS `echFillAll`, THE STUDIO'S OWN FILL — not a preview-flavoured copy of
 * it. Every rule that makes a fill safe (Manual blocks left alone, a field at the
 * wrong level skipped, a field the user took over skipped, the ISC swap, the
 * amenity preselect) is one implementation, and a second one here would be the
 * next thing to drift out of step with Content Builder.
 *
 * TWO GUARDS, BOTH DELIBERATE:
 *
 *  · NO SCOPE → NO FILL. `echDynPatch` treats Dynamic as authoritative *including
 *    when it resolves to nothing* — correct on a canvas the requester is editing
 *    (a stale "Atlanta" after switching focus is worse than an empty header), and
 *    wrong on a rail they have not answered anything for yet, where it would blank
 *    every placeholder the moment the form loaded. `echScopeReady` is the existing
 *    "has Step 1 actually been answered at this level" test, so the rail keeps the
 *    template's own defaults until there is something real to put in their place.
 *  · `ctx.pending` is left to `echDynPatch`, which already refuses to write an
 *    empty over real copy while a feed is in flight. Nothing is re-implemented.
 *
 * THE PASS CONVERGES, like the Studio's. `echFillAll` returns a patch only for the
 * blocks that CHANGED, and a patch can make the next one resolvable (a pick key
 * written on one pass feeds the fields read on the next). StudioView gets the
 * re-run for free because it patches React state and its effect re-fires on
 * `blocks`; a one-shot render has no such loop, so it loops here — capped, and
 * exiting the moment a pass writes nothing.
 */
/**
 * The focus string that produces each level — `focus` is the ONLY input to
 * `echLevel()`, so downgrading it is how the rail asks for a shallower fill without a
 * second code path.
 */
const FOCUS_FOR: Record<string, string> = {
  community: "Community", aoi: "Area of Interest", market: "Market", division: "Division",
};
/** most specific first: the rail fills at the deepest level Step 1 has ANSWERED */
const LEVEL_ORDER = ["community", "aoi", "market", "division"];

/**
 * FILL AT THE DEEPEST LEVEL STEP 1 HAS ANSWERED, NOT ONLY AT THE NOMINAL ONE
 * (2026-08-20 — Jack: *"it should show by selecting a division"*, then *"want to make
 * sure the preview reflects all of the dynamic content"*).
 *
 * The gate here used to be all-or-nothing: `echScopeReady(echLevel(focus, picks))`. So
 * with the focus set to Communities / Masterplans and no community picked yet, NOTHING
 * filled — including the things that only ever needed the division. Measured on JAX: the
 * Studio canvas showed the real division phone `904-380-0777` while the rail still read
 * `000-000-0000` and never mentioned Jacksonville.
 *
 * Nothing new decides what may fill. Every `ECH_DYN` field already declares the `levels`
 * it is allowed to fill at, so filling at `division` leaves a `levels:["community"]`
 * field untouched and its placeholder stands. All this does is stop refusing the whole
 * pass because the DEEPEST level is unanswered.
 */
/**
 * Keys where a BLANK is an instruction rather than a wiped placeholder.
 *
 * The listing block's structural keys came first: an empty `home3Plan` or
 * `collection2Name` makes the renderer DROP that row or header, so keeping Layne's
 * sample copy would show fake homes on a customer-facing preview.
 *
 * AMENITY ICON/LABEL PAIRS JOINED THEM 2026-08-21 (Jack: "make sure the email
 * preview auto populates the amenity as well .. and if no amenities leave as
 * blank"). `amenTrimHtml` treats an empty icon the same way — it removes the cell,
 * then the row — so on a community that states no amenities the rail has to accept
 * the blank. Without this the Studio canvas cleared while the form's preview rail,
 * the surface the requester actually looks at, kept the shipped placeholder icons.
 */
/*
 * THE HEADER'S LOCATOR SEGMENTS JOINED 2026-08-24 (Jack, off the live dev site: "the
 * issue is market or division level when i look at it"). Same shape as the two cases
 * above: `ECH_Logo` delimits `community | cityState | phone` with segment markers and
 * the renderer DROPS an emptied segment and its leading pipe, so an empty here is a
 * deletion the renderer acts on — not a gap to paper over with Layne's copy. Without
 * it a division or market send kept "Community Name" on the rail, which is exactly
 * the placeholder the requester reported seeing in their email.
 */
/*
 * THE FEATURED CARD'S OWN FIELDS JOINED 2026-09-10 (Jack: "Featured homes card view isn't
 * populating the preview the same as the creative studio view"). The canvas treats the
 * card's feed as authoritative — a card whose home resolves to nothing blanks — while the
 * rail kept Layne's placeholder plan in the same slot, so the two surfaces disagreed in
 * card view and agreed in table view. Same shape as the three cases above: these keys are
 * the feed's to write, an empty is the feed's answer, and the empty-image rule already
 * hides a blank photo. The whitelist is exactly the card's fed field set (FEAT*_HOMES /
 * FEAT*_PLANS), never the button labels or URLs.
 */
export const railBlankOk = (k: string): boolean =>
  /^(collection\d+Name|home\d+[A-Za-z]+|__lots|icon\d+|label\d+|community|cityState|PlanName|PlanImage|PlanImageAlt|Beds|Baths|SqFt|Price)$/.test(k);

export function echPreviewHtmlFor(name: string, ctx?: EchCtx | null): string {
  return echPreviewForOf(name, ctx).html;
}

/** the template assembled, filled and rendered — html AND the doc it came from */
export function echPreviewForOf(name: string, ctx?: EchCtx | null): { html: string; doc: DynBlock[] } {
  /* the template's OWN shell (2026-10-06) — a gallery card for a template that names
     one must not render in the request's current shell */
  return echPreviewOf(echDoc(name, pvBlock), ctx, echShellFor(name));
}

/**
 * THE SAME PREVIEW, FROM A DOCUMENT THE CALLER ALREADY HAS (2026-08-24).
 *
 * `echPreviewHtmlFor` assembles a FRESH doc from the template, which is right on the
 * form before anything has been edited and wrong afterwards: the Studio's edits live in
 * App's `blocks`, so a rail built from the template could not show them. The form now
 * passes its own blocks here when they are an ECH document, which makes one code path
 * responsible for the fill, the card expansion AND the render — so the rail and the
 * Studio canvas cannot disagree about either values or card count. Found by driving both
 * surfaces: the canvas showed two plan cards (Halle II / Georgia) while the rail showed
 * one, because the expansion lived in StudioView's effect and the rail was rendering
 * App's un-expanded block state.
 *
 * The doc is mutated in place, so callers pass a COPY of anything React owns.
 */
export function echPreviewHtmlOf(docIn: DynBlock[] | null | undefined, ctx?: EchCtx | null): string {
  return echPreviewOf(docIn, ctx).html;
}

/**
 * THE FILLED DOCUMENT AND ITS HTML, FROM ONE PASS (2026-08-25).
 *
 * Uche, on the live dev site: "the preview pane is not getting updated with my actual
 * selections, so the email that gets created in SFMC looks incorrect." The rail and the
 * SUBMIT were reading two different things — the rail this function, and the submit
 * App's raw `renderDocument(blocks)`, gated on a STARTER-divergence test that is false
 * for anyone who never opened the Creative Studio. So a request built entirely on the
 * form shipped SFMC the bare template.
 *
 * Returning the doc as well as the html lets the form send the SAME object it shows:
 * one fill, one expansion, one render, two consumers.
 */
export function echPreviewOf(docIn: DynBlock[] | null | undefined, ctx?: EchCtx | null, shell?: string | null): { html: string; doc: DynBlock[] } {
  try {
    const doc = docIn as ReturnType<typeof echDoc>;
    if (!doc || !doc.length) return { html: "", doc: [] };
    /* freshly assembled above and owned entirely by this call, so mutating it in
       place is safe here in a way it never is in the Studio (React owns that doc) */
    const want = ctx ? echLevel(ctx.focus, (ctx.picks || []).length) : "";
    /* the nominal level if it's answered, else the deepest one that is */
    const use = !ctx ? "" : echScopeReady(want, ctx)
      ? want
      : (LEVEL_ORDER.slice(LEVEL_ORDER.indexOf(want) + 1).find((l) => echScopeReady(l, ctx)) || "");
    if (ctx && use) {
      const fillCtx = use === want ? ctx : { ...ctx, focus: FOCUS_FOR[use] || ctx.focus };
      /* ── THE RAIL GROWS THE SAME CARDS THE STUDIO DOES (2026-08-24) ───────────────
         One plan card per community is a block INSERT, not a fill (see
         `echPlanCardsNeeded`). The Studio does it to React's block state; the rail does
         it to this locally-owned doc, so the two renderings of one email cannot
         disagree about how many cards there are — the split this file exists to prevent.
         No `__cardsAuto` stamp here: the rail is rebuilt from the template on every
         pass, so there is nothing to re-add and nothing for a requester to delete. */
      /* the rail sheds the same unfillable cards the Studio does (2026-09-10) — the
         two renderings must agree on how many cards there are */
      const drop = new Set(echPlanCardsExcess(doc as unknown as DynBlock[], fillCtx));
      if (drop.size) for (let i = doc.length - 1; i >= 0; i--) if (drop.has(doc[i].id)) doc.splice(i, 1);
      const need = echPlanCardsNeeded(doc as unknown as DynBlock[], fillCtx);
      if (need > 0) {
        const at = echLastPlanCardIdx(doc as unknown as DynBlock[]);
        const src = doc[at];
        if (src) {
          const add = [];
          for (let i = 0; i < need; i++) {
            /* FRESH per-block state, not a copy of card 1 (audit 2026-08-24). Copying
               `data` carried `__planPick` (so every rail clone showed card 1's explicitly
               picked plan while the Studio clones mapped to communities 2 and 3),
               plus `__socOff` / `__socFed` (which SKIP the fill) and card 1's
               already-filled values. The Studio clones start from registry defaults; the
               rail now starts from the same place, so the two renderings agree. */
            /* CORRECTED: strip only the per-REQUESTER state, never the structural keys.
               `__html` IS the block's SFMC markup (echRegistry defaults) — dropping every
               `__` key rendered the clone as an empty row, which is why the canvas showed
               two plan cards and the rail one. Measured with a page-context probe: the
               rail computed `need: 1` and spliced correctly; the clone just had nothing
               to render. */
            const DROP = /^(__planPick|__homePick|__socOff|__socFed|__lots|__cardsAuto)$/;
            const fresh: Record<string, unknown> = {};
            Object.keys(src.data).forEach((k) => { if (!DROP.test(k)) fresh[k] = (src.data as Record<string, unknown>)[k]; });
            fresh[ECH_FEAT_KEY] = String((src.data as Record<string, unknown>)[ECH_FEAT_KEY] || "plans");
            add.push({ ...src, id: src.id + "-c" + (i + 2), data: fresh });
          }
          doc.splice(at + 1, 0, ...(add as typeof doc));
        }
      }
      for (let pass = 0; pass < 4; pass++) {
        const patches = echFillAll(doc as unknown as DynBlock[], fillCtx);
        const ids = Object.keys(patches);
        if (!ids.length) break;
        for (const b of doc) {
          const p = patches[b.id];
          if (!p) continue;
          /* A BLANK NEVER REPLACES A PLACEHOLDER ON THE RAIL — WHILE THE FEED IS STILL
             ANSWERING. `echDynPatch` treats Dynamic as authoritative including when it
             resolves to nothing — right on a canvas the requester is editing, wrong on a
             form they are still filling in, where it would wipe Layne's shipped copy the
             moment the division was picked and the packs were still on their way.
             ── BUT ONCE THE FEED HAS SETTLED, THE RAIL AGREES WITH THE STUDIO (2026-09-14) ──
             Jie (CRS), two unrelated masterplans on a Grand Opening Thank You: the rail's
             hero read the block's shipped "Community Name" while the Studio, one click
             away, had the same field blank — two renderings of one email disagreeing, and
             the rail was the one lying (a two-community send has no single name; the
             Studio's blank is the honest answer, and the required gate asks for the pick).
             So a TEXT placeholder is protected only while `ctx.pending`; a settled empty
             is written, exactly as the Studio writes it. A url or image keeps the
             protection at every moment — a blank href is a dead link and a blank src a
             broken picture on a customer-facing preview, and the required gate is what
             names those, not the rail. */
          const settled = !ctx.pending;
          const declared = (echMeta(b.type)?.declared || {}) as Record<string, { type?: string } | undefined>;
          const linkOrImage = (k: string) => { const t = String((declared[k] && declared[k]!.type) || ""); return t === "url" || t === "image" || /url|href|src/i.test(k); };
          const keep: Record<string, unknown> = {};
          for (const k of Object.keys(p)) {
            const next = String(p[k] ?? "");
            const now = String((b.data as Record<string, unknown>)[k] ?? "");
            /* …EXCEPT the listing table's STRUCTURAL keys (2026-08-21, Jack's rail
               screenshot: a real fill above a shipped "Collection Name" header and a
               "1001 / Plan Name" sample row). For home rows and collection headers an
               empty is a DELETION the renderer acts on — dropEmptyHomeRows /
               dropEmptyCollectionHeaders — so keeping the sample copy shows fake
               homes on a customer-facing preview. Copy placeholders everywhere else
               keep the original protection (while pending). */
            if (next === "" && now !== "" && !railBlankOk(k) && (!settled || linkOrImage(k))) continue;
            keep[k] = p[k];
          }
          if (Object.keys(keep).length) b.data = { ...b.data, ...keep };
        }
      }
    }
    return { html: renderDocument(doc, shell === null ? undefined : shell), doc: doc as unknown as DynBlock[] };
  } catch {
    return { html: "", doc: [] };
  }
}
